/**
 * Utility functions for Solana staking validation.
 * @internal
 */
import { SolanaStakingReportingApi, createStakingClient, } from "@blockdaemon-internal/staking-reporting-ts-client";
/** Default lookback window for APY queries: 30 days in seconds. */
export const APY_DEFAULT_LOOKBACK_SECONDS = 30 * 24 * 3600;
/**
 * Creates a SolanaStakingReportingApi instance. Used by SolanaStakingProtocol constructor.
 * Tests may use this to obtain an API for standalone calls.
 */
export function createSolanaStakingReportingApi(bdApiKey, basePath, logger) {
    if (!bdApiKey || bdApiKey.trim() === "") {
        logger.warn("Blockdaemon API key is not set for Solana staking reporting API.");
    }
    return new SolanaStakingReportingApi(createStakingClient({
        apiKey: bdApiKey,
        baseUrl: `${basePath}/reporting/staking`,
    }).getConfiguration());
}
/**
 * Validates Solana address format (Base58, 32-44 characters).
 * Base58 alphabet: 123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz

 * @param address - The address to validate
 * @returns True if the address is a valid Solana address format
 *
 * @example
 * ```typescript
 * isValidSolanaAddressFormat("FQwewNXahV7MiZcLpY6p1xhUs2acVGQ3U5Xxc7FzV571"); // true
 * isValidSolanaAddressFormat("invalid"); // false
 * isValidSolanaAddressFormat("0x742d35Cc6634C0532925a3b844"); // false (Ethereum format)
 * ```
 */
function isValidSolanaAddressFormat(address) {
    if (!address || address.startsWith("0x") || address.length < 32 || address.length > 44) {
        return false;
    }
    const base58Regex = /^[1-9A-HJ-NP-Za-km-z]+$/;
    return base58Regex.test(address);
}
/**
 * Validates vote keys in transaction requests/responses against allowed list
 * set in Solana protocol config.
 *
 * @remarks
 * - Returns true (any vote key allowed) if no config is provided
 * - Returns true if the allow list is empty
 * - Validates the vote key format before checking the allow list
 *
 * @param network - The Solana network in CAIP-2 format ("solana:mainnet" or "solana:testnet")
 * @param validatorVoteKey - The validator vote public key to validate
 * @param solanaConfig - Optional Solana configuration with allow lists
 * @returns True if the validator vote key is allowed
 * @throws Error if validatorVoteKey is empty, invalid format, or network is unsupported
 */
export function _isValidatorVoteKeyAllowed(network, validatorVoteKey, solanaConfig) {
    if (!validatorVoteKey) {
        throw new Error("Validator vote pubkey (vaultAddress) cannot be empty");
    }
    // Validate the vote key format
    if (!isValidSolanaAddressFormat(validatorVoteKey)) {
        throw new Error(`Invalid validator vote pubkey format: ${validatorVoteKey}. Must be a valid Solana address (Base58, 32-44 characters).`);
    }
    if (network !== "solana:mainnet" && network !== "solana:testnet") {
        throw new Error(`Unsupported network for Solana: ${network}`);
    }
    // If no config provided, allow all validators
    if (!solanaConfig) {
        return true;
    }
    // Check against allow list for the specific network
    const allowList = network === "solana:mainnet"
        ? solanaConfig.customerMainnetValidatorVotePubKeys
        : solanaConfig.customerTestnetValidatorVotePubKeys;
    if (allowList && allowList.length > 0) {
        return allowList.includes(validatorVoteKey);
    }
    return true;
}
//# sourceMappingURL=data:application/json;base64,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